import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link as RouterLink, type LinkProps } from 'react-router';

/**
 * Button — the pill-shaped, brand-token-driven button primitive.
 *
 * Four variants:
 *
 *   primary    — violet fill, dark text. The default CTA.
 *                Used everywhere a user is creating / saving / moving forward.
 *
 *   secondary  — green container, dark text. Reserved for monetisation /
 *                upgrade CTAs and "hot" links per DESIGN.md. Use sparingly
 *                so it keeps its meaning.
 *
 *   ghost      — transparent fill, glass border, on-surface text. Toolbar
 *                actions, cancel buttons, secondary nav.
 *
 *   glass      — `surface-glass` fill + backdrop-blur + primary glow on
 *                hover. Hero-CTA over a mesh / image background.
 *
 *   destructive — error-tinted, for destructive confirmation buttons.
 *
 * Sizes (height per DESIGN.md):
 *   sm — 36px height, label-sm, used inline in toolbars
 *   md — 44px height (default), label-md
 *   lg — 56px height, body-lg, hero / wizard primary
 *
 * `asLink` prop swaps the element to a React Router <Link>, keeping the
 * same visual styling — avoids the "button-shaped <a>" hack callers
 * tend to write inline.
 */

type Variant = 'primary' | 'secondary' | 'ghost' | 'glass' | 'destructive';
type Size = 'sm' | 'md' | 'lg';

const VARIANT_CLASS: Record<Variant, string> = {
  primary:
    'bg-[var(--color-primary)] text-[var(--color-primary-foreground)] hover:brightness-110 active:brightness-95',
  secondary:
    'bg-[var(--color-secondary)] text-[var(--color-on-secondary)] hover:brightness-110 active:brightness-95',
  ghost:
    'bg-transparent text-[var(--color-foreground)] border border-[var(--color-border)] hover:bg-[var(--surface-glass)]',
  glass:
    'glass text-[var(--color-foreground)] hover:bg-[var(--surface-glass-strong)] hover:shadow-[var(--glow-primary)]',
  destructive:
    'bg-[var(--color-error-container)] text-[var(--color-on-error-container)] hover:brightness-110 active:brightness-95',
};

const SIZE_CLASS: Record<Size, string> = {
  sm: 'h-9 px-4 text-sm font-medium',
  md: 'h-11 px-6 text-sm font-medium',
  lg: 'h-14 px-8 text-base font-semibold',
};

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-full select-none transition disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-[var(--color-ring)] focus-visible:outline-offset-2 whitespace-nowrap';

interface CommonProps {
  variant?: Variant;
  size?: Size;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  fullWidth?: boolean;
}

type ButtonProps = CommonProps & ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', leadingIcon, trailingIcon, fullWidth, className = '', children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={`${BASE} ${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]} ${fullWidth ? 'w-full' : ''} ${className}`}
      {...rest}
    >
      {leadingIcon && <span aria-hidden>{leadingIcon}</span>}
      {children}
      {trailingIcon && <span aria-hidden>{trailingIcon}</span>}
    </button>
  );
});

type ButtonLinkProps = CommonProps & LinkProps & { className?: string };

/**
 * Same visual API as <Button> but renders a react-router <Link>. Use this
 * for navigation CTAs ("Start free", "+ New link", etc.) so we don't have
 * to choose between "looks like a button" and "is a real link."
 */
export function ButtonLink({
  variant = 'primary',
  size = 'md',
  leadingIcon,
  trailingIcon,
  fullWidth,
  className = '',
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <RouterLink
      className={`${BASE} ${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]} ${fullWidth ? 'w-full' : ''} ${className}`}
      {...rest}
    >
      {leadingIcon && <span aria-hidden>{leadingIcon}</span>}
      {children}
      {trailingIcon && <span aria-hidden>{trailingIcon}</span>}
    </RouterLink>
  );
}
